import { supabase } from "../../config/supabase";

export interface Crop {
  id: string;
  crop_name: string;
  nitrogen_n: number;
  phosphorus_p2o5: number;
  potassium_k2o: number;
  calcium_cao: number;
  magnesium_mgo: number;
  sulfur_so3: number;
}

interface CropData {
  crop: string;
  countN: number;
  countP2O5: number;
  countK2O: number;
  countCaO: number;
  countMgO: number;
  countSO3: number;
}

export class CropService {
  async listCrops() {
    try {
      const { data, error } = await supabase
        .from("crops")
        .select("*")
        .order("crop_name", { ascending: true });

      if (error) throw error;
      return {
        success: true,
        data: (data as Crop[]).map((crop) => ({
          crop: crop.crop_name,
          countN: crop.nitrogen_n,
          countP2O5: crop.phosphorus_p2o5,
          countK2O: crop.potassium_k2o,
          countCaO: crop.calcium_cao,
          countMgO: crop.magnesium_mgo,
          countSO3: crop.sulfur_so3,
          cropId: crop.id,
        })),
      };
    } catch (error) {
      console.error("Error listing crops:", (error as Error).message);
      return {
        success: false,
        error: (error as Error).message,
      };
    }
  }

  async createCrop(cropData: CropData) {
    try {
      const { data: existingCrops } = await supabase
        .from("crops")
        .select("id")
        .order("id", { ascending: false })
        .limit(1);

      const { data, error } = await supabase
        .from("crops")
        .insert({
          crop_name: cropData.crop,
          nitrogen_n: cropData.countN,
          phosphorus_p2o5: cropData.countP2O5,
          potassium_k2o: cropData.countK2O,
          calcium_cao: cropData.countCaO,
          magnesium_mgo: cropData.countMgO,
          sulfur_so3: cropData.countSO3,
          user_id: (await supabase.auth.getUser()).data.user?.id,
        })
        .select()
        .single();

      if (error) throw error;

      return {
        success: true,
        data: {
          crop: (data as Crop).crop_name,
          countN: (data as Crop).nitrogen_n,
          countP2O5: (data as Crop).phosphorus_p2o5,
          countK2O: (data as Crop).potassium_k2o,
          countCaO: (data as Crop).calcium_cao,
          countMgO: (data as Crop).magnesium_mgo,
          countSO3: (data as Crop).sulfur_so3,
          id: (data as Crop).id,
        },
      };
    } catch (error) {
      console.error("Error creating crop:", (error as Error).message);
      return {
        success: false,
        error: (error as Error).message,
      };
    }
  }

  async updateCrop(id: string, cropData: CropData) {
    try {
      const { data, error } = await supabase
        .from("crops")
        .update({
          crop_name: cropData.crop,
          nitrogen_n: cropData.countN,
          phosphorus_p2o5: cropData.countP2O5,
          potassium_k2o: cropData.countK2O,
          calcium_cao: cropData.countCaO,
          magnesium_mgo: cropData.countMgO,
          sulfur_so3: cropData.countSO3,
        })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;

      return {
        success: true,
        data: {
          crop: (data as Crop).crop_name,
          countN: (data as Crop).nitrogen_n,
          countP2O5: (data as Crop).phosphorus_p2o5,
          countK2O: (data as Crop).potassium_k2o,
          countCaO: (data as Crop).calcium_cao,
          countMgO: (data as Crop).magnesium_mgo,
          countSO3: (data as Crop).sulfur_so3,
          id: (data as Crop).id,
        },
      };
    } catch (error) {
      console.error("Error updating crop:", (error as Error).message);
      return {
        success: false,
        error: (error as Error).message,
      };
    }
  }

  async deleteCrop(id: string) {
    try {
      const { error } = await supabase.from("crops").delete().eq("id", id);

      if (error) throw error;

      return {
        success: true,
      };
    } catch (error) {
      console.error("Error deleting crop:", (error as Error).message);
      return {
        success: false,
        error: (error as Error).message,
      };
    }
  }

  async existsCropByName(crop_name: string) {
    try {
      const { data, error } = await supabase
        .from("crops")
        .select("id")
        .ilike("crop_name", crop_name);
      if (error) {
        console.error("Error checking crop by name:", error);
        return { success: false, error };
      }
      return { success: true, exists: Array.isArray(data) && data.length > 0 };
    } catch (error) {
      console.error("Error in existsCropByName:", error);
      return { success: false, error };
    }
  }
}

export const cropService = new CropService();
